import React, { useMemo, useState } from "react";

const fmt = (x) =>
  Number(x || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// One triangle covering BOTH gaps, because they are the same kind of
// problem seen from either side of the margin: hours nobody priced earn
// nothing, hours nobody costed cost nothing. Reporting only the first —
// as this table used to — flatters every margin whose cost rate is
// missing, and says nothing at all about it.
export function MissingRatesFlag({ rates = 0, costs = 0 }) {
  if (!(rates > 0) && !(costs > 0)) return null;
  const parts = [];
  if (rates > 0) parts.push(`${fmt(rates)} h without a selling rate (no revenue)`);
  if (costs > 0) parts.push(`${fmt(costs)} h without a cost rate (no direct cost)`);
  const text = parts.join(" · ");
  return (
    <span
      title={text}
      aria-label={`Warning: ${text}`}
      style={{
        flexShrink: 0,
        color: "#f97316",
        fontSize: 12,
        lineHeight: 1,
        cursor: "help",
      }}
    >
      ⚠
    </span>
  );
}

export default function ClientTreemapTable({ data = [], hoveredItem, setHoveredItem }) {
  const [sortKey, setSortKey] = useState(null);
  const [direction, setDirection] = useState("desc");

  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      const av = a[sortKey] || 0;
      const bv = b[sortKey] || 0;
      if (direction === "asc") return av - bv;
      return bv - av;
    });
  }, [data, sortKey, direction]);

  function toggleSort(key) {
    if (sortKey === key) {
      setDirection((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setDirection("desc");
    }
  }

  function handleSortKeyDown(e, key) {
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      toggleSort(key);
    }
  }

  const sortAriaLabel = (key, label) => {
    if (sortKey !== key) return `Sort by ${label}`;
    const next = direction === "desc" ? "ascending" : "descending";
    return `${label}, sorted ${direction === "desc" ? "descending" : "ascending"}. Activate to sort ${next}.`;
  };

  const ariaSort = (key) =>
    sortKey === key ? (direction === "desc" ? "descending" : "ascending") : "none";

  const total = useMemo(() => {
    return sortedData.reduce(
      (acc, r) => {
        acc.hours             += r.hours             || 0;
        acc.revenue           += r.revenue           || 0;
        acc.directCost        += r.directCost        || 0;
        acc.overhead          += r.overhead          || 0;
        acc.cost              += r.cost              || 0;
        acc.margin            += r.margin            || 0;
        // Aggregate the warnings too: without these the problem vanished at
        // exactly the row a reader checks first, and a handful of flagged
        // clients summed to a clean-looking TOTAL.
        acc.hoursMissingRates += r.hoursMissingRates || 0;
        acc.hoursMissingCosts += r.hoursMissingCosts || 0;
        return acc;
      },
      { hours: 0, revenue: 0, directCost: 0, overhead: 0, cost: 0, margin: 0,
        hoursMissingRates: 0, hoursMissingCosts: 0 }
    );
  }, [sortedData]);

  const arrow = (key) => sortKey === key ? (direction === "desc" ? " ↓" : " ↑") : "";

  function getMarginColor(m) {
    // Absolute-euro banding. Ordered high -> low so every branch is reachable:
    // green for strong margins, orange for the middle band, red for weak/negative.
    if (m >= 1500) return "#1D9E75";
    if (m >= 500) return "#f97316";
    return "#e03e3e";
  }

  const thStyle = {
    padding: "8px 6px",
    fontSize: 13,
    fontWeight: 600,
    color: "#6b7280",
    borderBottom: "1px solid #e5e7eb",
    cursor: "pointer",
    whiteSpace: "nowrap",
    textAlign: "right",
  };

  const tdStyle = {
    padding: "7px 6px",
    fontSize: 13,
    color: "#111827",
    borderBottom: "1px solid #f3f4f6",
    textAlign: "right",
  };

  return (
    <div style={{
      background: "#ffffff",
      border: "1px solid #e5e7eb",
      borderRadius: 10,
      padding: 16,
      minWidth: 0,
      overflow: "hidden",
    }}>
      {/* Cabecera del panel */}
      <div style={{ marginBottom: 12 }}>
        <span style={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "#6b7280",
        }}>
          Metrics by client
        </span>
      </div>

      <table style={{
        width: "100%",
        borderCollapse: "collapse",
        tableLayout: "fixed",
      }}>
        <colgroup>
          <col style={{ width: "18%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "13%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "13%" }} />
          <col style={{ width: "18%" }} />
        </colgroup>

        <thead>
          <tr>
            <th style={{ ...thStyle, textAlign: "left" }}>Client</th>
            <th
              style={thStyle}
              onClick={() => toggleSort("hours")}
              onKeyDown={(e) => handleSortKeyDown(e, "hours")}
              tabIndex={0}
              aria-label={sortAriaLabel("hours", "Hours")}
              aria-sort={ariaSort("hours")}
            >Hours{arrow("hours")}</th>
            <th
              style={thStyle}
              onClick={() => toggleSort("revenue")}
              onKeyDown={(e) => handleSortKeyDown(e, "revenue")}
              tabIndex={0}
              aria-label={sortAriaLabel("revenue", "Revenue")}
              aria-sort={ariaSort("revenue")}
            >Revenue{arrow("revenue")}</th>
            <th
              style={thStyle}
              onClick={() => toggleSort("directCost")}
              onKeyDown={(e) => handleSortKeyDown(e, "directCost")}
              tabIndex={0}
              aria-label={sortAriaLabel("directCost", "Direct cost")}
              aria-sort={ariaSort("directCost")}
            >Direct cost{arrow("directCost")}</th>
            <th
              style={thStyle}
              onClick={() => toggleSort("overhead")}
              onKeyDown={(e) => handleSortKeyDown(e, "overhead")}
              tabIndex={0}
              aria-label={sortAriaLabel("overhead", "Overhead")}
              aria-sort={ariaSort("overhead")}
            >Overhead{arrow("overhead")}</th>
            <th
              style={thStyle}
              onClick={() => toggleSort("cost")}
              onKeyDown={(e) => handleSortKeyDown(e, "cost")}
              tabIndex={0}
              aria-label={sortAriaLabel("cost", "Total cost")}
              aria-sort={ariaSort("cost")}
            >Total cost{arrow("cost")}</th>
            <th
              style={thStyle}
              onClick={() => toggleSort("margin")}
              onKeyDown={(e) => handleSortKeyDown(e, "margin")}
              tabIndex={0}
              aria-label={sortAriaLabel("margin", "Margin")}
              aria-sort={ariaSort("margin")}
            >Margin{arrow("margin")}</th>
          </tr>
        </thead>

        <tbody>
          {sortedData.map((r) => (
            <tr
              key={r.name}
              onMouseEnter={() => setHoveredItem?.(r.name)}
              onMouseLeave={() => setHoveredItem?.(null)}
              style={{
                background: hoveredItem === r.name ? "#f0fdf8" : "transparent",
                transition: "background 0.15s ease",
                position: "relative",
              }}
            >
              <td style={{ ...tdStyle, textAlign: "left", position: "relative", overflow: "visible" }}>
                <span style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}>
                  <span style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}>
                    {r.name}
                  </span>
                  <MissingRatesFlag
                    rates={r.hoursMissingRates}
                    costs={r.hoursMissingCosts}
                  />
                </span>
                {(r.name === "Milcho Ubichev" || r.name === "EXERTUM") &&
                  hoveredItem === r.name && (
                    <div style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      background: "rgba(0,0,0,0.85)",
                      color: "#fff",
                      padding: "6px 10px",
                      borderRadius: 6,
                      fontSize: 12,
                      whiteSpace: "nowrap",
                      zIndex: 999,
                      marginTop: 2,
                      pointerEvents: "none",
                    }}>
                      Total hours may contain standby shifts
                    </div>
                  )}
              </td>
              <td style={tdStyle}>{fmt(r.hours)}</td>
              <td style={tdStyle}>{fmt(r.revenue)}</td>
              <td style={tdStyle}>{fmt(r.directCost)}</td>
              <td style={tdStyle}>{fmt(r.overhead)}</td>
              <td style={tdStyle}>{fmt(r.cost)}</td>
              <td style={{ ...tdStyle, fontWeight: 600, color: getMarginColor(r.margin) }}>
                {fmt(r.margin)}
              </td>
            </tr>
          ))}

          {/* TOTAL */}
          <tr style={{ background: "#f9fafb" }}>
            <td style={{ ...tdStyle, textAlign: "left", fontWeight: 700, borderTop: "2px solid #e5e7eb", borderBottom: "none" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                TOTAL
                <MissingRatesFlag
                  rates={total.hoursMissingRates}
                  costs={total.hoursMissingCosts}
                />
              </span>
            </td>
            <td style={{ ...tdStyle, fontWeight: 700, borderTop: "2px solid #e5e7eb", borderBottom: "none" }}>{fmt(total.hours)}</td>
            <td style={{ ...tdStyle, fontWeight: 700, borderTop: "2px solid #e5e7eb", borderBottom: "none" }}>{fmt(total.revenue)}</td>
            <td style={{ ...tdStyle, fontWeight: 700, borderTop: "2px solid #e5e7eb", borderBottom: "none" }}>{fmt(total.directCost)}</td>
            <td style={{ ...tdStyle, fontWeight: 700, borderTop: "2px solid #e5e7eb", borderBottom: "none" }}>{fmt(total.overhead)}</td>
            <td style={{ ...tdStyle, fontWeight: 700, borderTop: "2px solid #e5e7eb", borderBottom: "none" }}>{fmt(total.cost)}</td>
            <td style={{ ...tdStyle, fontWeight: 700, color: getMarginColor(total.margin), borderTop: "2px solid #e5e7eb", borderBottom: "none" }}>
              {fmt(total.margin)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}